<script setup lang="ts">
import { computed } from 'vue'

import BetaProgressTaskItem from '@/components/beta-dashboard/molecules/BetaProgressTaskItem.vue'
import type { BetaDashboardTask } from '@/types/beta-dashboard'

const props = defineProps<{
    tasks: BetaDashboardTask[]
    progress: number | null
    loading?: boolean
}>()

const emit = defineEmits<{
    action: [task: BetaDashboardTask]
}>()

const requiredTasks = computed(() => props.tasks.filter((task) => task.mandatory))
const completedTasks = computed(() => (
    requiredTasks.value.filter((task) => task.state === 'complete').length
))
const nextTask = computed(() => (
    requiredTasks.value.find((task) => task.state === 'pending')
    ?? requiredTasks.value.find((task) => task.state === 'unknown')
    ?? requiredTasks.value.find((task) => task.state === 'unsupported')
    ?? null
))
const isReady = computed(() => props.progress === 100)
const normalizedProgress = computed(() => (
    props.progress === null ? 0 : Math.min(100, Math.max(0, props.progress))
))
const progressStyle = computed(() => ({
    width: `${normalizedProgress.value}%`,
}))
</script>

<template>
    <section class="beta-goals">
        <div class="beta-goals__summary">
            <header class="beta-goals__header">
                <div class="beta-goals__copy">
                    <div class="beta-goals__eyebrow-row">
                        <p>Metas do seu site</p>
                        <span>{{ completedTasks }}/{{ requiredTasks.length }} essenciais</span>
                    </div>

                    <h2>
                        {{ isReady
                            ? 'Tudo pronto para receber novos clientes'
                            : 'Complete o essencial para colocar seu site no ar'
                        }}
                    </h2>
                    <p>
                        {{ isReady
                            ? 'As metas principais foram concluídas. Você ainda pode revisar os itens opcionais.'
                            : 'Avance uma meta por vez. O progresso considera somente os itens essenciais.'
                        }}
                    </p>
                </div>

                <div class="beta-goals__percentage" aria-hidden="true">
                    <strong>{{ progress === null ? '—' : `${progress}%` }}</strong>
                    <span>{{ progress === null ? 'indisponível' : 'concluído' }}</span>
                </div>
            </header>

            <div class="beta-goals__progress-block">
                <div
                    :class="['beta-goals__track', loading && 'is-loading']"
                    role="progressbar"
                    aria-label="Progresso das metas essenciais"
                    aria-valuemin="0"
                    aria-valuemax="100"
                    :aria-valuenow="progress ?? undefined"
                >
                    <span v-if="!loading" :style="progressStyle" />
                </div>

                <div class="beta-goals__progress-caption">
                    <span v-if="nextTask">
                        Próxima meta: <strong>{{ nextTask.title }}</strong>
                    </span>
                    <span v-else-if="isReady">Metas essenciais concluídas</span>
                    <span v-else>Confira os itens ao lado</span>
                    <small>Itens opcionais não alteram o percentual</small>
                </div>
            </div>

            <div
                class="beta-goals__astronaut"
                role="img"
                aria-label="Astronauta flutuando enquanto seu site ganha forma"
            />
        </div>

        <div class="beta-goals__checklist">
            <div class="beta-goals__tasks">
                <BetaProgressTaskItem
                    v-for="(task, index) in tasks"
                    :key="task.id"
                    :task="task"
                    :position="index + 1"
                    :next="task.id === nextTask?.id"
                    @action="emit('action', $event)"
                />
            </div>
        </div>
    </section>
</template>

<style scoped>
.beta-goals {
    display: grid;
    grid-template-columns: minmax(300px, 0.8fr) minmax(0, 1.45fr);
    align-items: stretch;
    overflow: hidden;
    border: 1px solid #dfe5ec;
    border-radius: 24px;
    background: #fff;
}

.beta-goals__summary {
    display: grid;
    min-width: 0;
    align-content: start;
}

.beta-goals__checklist {
    min-width: 0;
    border-left: 1px solid #e8edf2;
}

.beta-goals__header {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: start;
    gap: 16px;
    padding: 22px 22px 16px;
}

.beta-goals__eyebrow-row {
    display: flex;
    align-items: center;
    gap: 10px;
}

.beta-goals__eyebrow-row p {
    color: var(--beta-accent, var(--main-color, #2f86f6));
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.18em;
    text-transform: uppercase;
}

.beta-goals__eyebrow-row span {
    border-radius: 999px;
    background: #f0f3f7;
    padding: 4px 8px;
    color: #728095;
    font-size: 10px;
    font-weight: 600;
}

.beta-goals__copy h2 {
    margin-top: 10px;
    max-width: 620px;
    color: #172132;
    font-family: 'Outfit', ui-sans-serif, system-ui, sans-serif;
    font-size: 20px;
    font-weight: 600;
    letter-spacing: -0.025em;
    line-height: 1.25;
}

.beta-goals__copy > p {
    margin-top: 7px;
    max-width: 600px;
    color: #747f8f;
    font-size: 11px;
    line-height: 1.5;
}

.beta-goals__percentage {
    display: grid;
    min-width: 64px;
    justify-items: end;
}

.beta-goals__percentage strong {
    color: #172132;
    font-family: 'Outfit', ui-sans-serif, system-ui, sans-serif;
    font-size: 26px;
    font-weight: 600;
    letter-spacing: -0.04em;
    line-height: 1;
}

.beta-goals__percentage span {
    margin-top: 4px;
    color: #8792a2;
    font-size: 10px;
    font-weight: 500;
    letter-spacing: 0.08em;
    text-transform: uppercase;
}

.beta-goals__progress-block {
    padding: 0 22px 22px;
}

.beta-goals__track {
    height: 7px;
    overflow: hidden;
    border-radius: 999px;
    background: #e9edf2;
}

.beta-goals__track > span {
    display: block;
    height: 100%;
    min-width: 7px;
    border-radius: inherit;
    background: var(--beta-accent, var(--main-color, #2f86f6));
    transition: width 240ms ease;
}

.beta-goals__track.is-loading {
    background: linear-gradient(90deg, #e7ebf0 25%, #f5f7f9 50%, #e7ebf0 75%);
    background-size: 200% 100%;
    animation: beta-goals-loading 1.3s linear infinite;
}

.beta-goals__progress-caption {
    display: grid;
    gap: 5px;
    margin-top: 8px;
    color: #728095;
    font-size: 11px;
}

.beta-goals__progress-caption strong {
    color: #3a4657;
    font-weight: 600;
}

.beta-goals__progress-caption small {
    color: #97a1af;
    font-size: 11px;
}

.beta-goals__astronaut {
    width: min(251px, calc(100% - 44px));
    aspect-ratio: 449 / 444;
    align-self: center;
    justify-self: center;
    overflow: hidden;
    margin: 2px 22px 22px;
    border-radius: 24px;
    background: url('@/assets/images/astronauta.gif') center / cover no-repeat;
}

:global(body.others .beta-goals__astronaut) {
    aspect-ratio: 425 / 480;
    background-image: url('@/assets/images/astronauta-verde.gif');
}

.beta-goals__list-heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-bottom: 1px solid #e8edf2;
    background: #f8fafc;
    padding: 10px 24px;
}

.beta-goals__list-heading strong {
    color: #3c4859;
    font-size: 12px;
    font-weight: 600;
}

.beta-goals__list-heading span {
    color: #8a95a4;
    font-size: 12px;
}

.beta-goals__tasks {
    display: grid;
    padding: 2px 16px;
}

.beta-goals__tasks > :last-child {
    border-bottom: 0;
}

@keyframes beta-goals-loading {
    to { background-position: -200% 0; }
}

@media (max-width: 900px) {
    .beta-goals {
        grid-template-columns: 1fr;
    }

    .beta-goals__checklist {
        border-top: 1px solid #e8edf2;
        border-left: 0;
    }

    .beta-goals__list-heading {
        border-top: 0;
    }
}

@media (max-width: 767px) {
    .beta-goals__header {
        gap: 16px;
        padding: 20px 18px 16px;
    }

    .beta-goals__percentage {
        min-width: 65px;
    }

    .beta-goals__percentage strong {
        font-size: 25px;
    }

    .beta-goals__progress-block {
        padding: 0 18px 18px;
    }

    .beta-goals__astronaut {
        width: min(210px, calc(100% - 36px));
        margin: 0 18px 18px;
    }

    .beta-goals__progress-caption {
        align-items: flex-start;
        flex-direction: column;
        gap: 4px;
    }

    .beta-goals__list-heading {
        padding: 10px 18px;
    }

    .beta-goals__tasks {
        padding: 4px 14px;
    }
}
</style>
